import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader, Card, Button, Field, Select } from "@/components/UI";
import {
  CloudArrowDown, FileXls, Database, ShieldCheck, ArrowsClockwise,
  FilePdf, UploadSimple, CheckCircle, XCircle, ArrowRight,
  ArrowCounterClockwise, PaperPlaneTilt, Warning,
} from "@phosphor-icons/react";
import { IMPORT_TYPES } from "./constants";

export function ExcelImportSection() {
  const [entity, setEntity] = useState("personnel");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const onFileChoose = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setResult(null);
    const fd = new FormData();
    fd.append("entity_type", entity);
    fd.append("file", f);
    try {
      const r = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Excel okunamadı");
      setFile(null);
    }
  };

  const apply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", entity);
      fd.append("file", file);
      const r = await api.post("/import/apply", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(r.data);
      toast.success(`✓ ${r.data.entity_label || "Kayıt"}: ${r.data.message}`);
      setPreview(null); setFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "İçe aktarım hatası");
    } finally {
      setApplying(false);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  const currentType = IMPORT_TYPES.find(t => t.id === entity);

  return (
    <div>
      <div className="mb-6 p-4 rounded-md border-2 border-[#DDA74F] bg-[#FDF5E5]" data-testid="import-info">
        <div className="flex items-start gap-3">
          <UploadSimple size={22} weight="duotone" className="text-[#8B6914] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#7a5a10] mb-1">📥 Excel'den Kolay Geçiş</div>
            <div className="text-sm text-[#5e675e] leading-relaxed">
              Eski Excel dosyalarınızı sisteme yükleyin. Sistem başlıkları otomatik tanır (Türkçe/İngilizce), önizleme gösterir, sonra tek tıkla içe aktarır. Yalnızca Admin.
            </div>
          </div>
        </div>
      </div>

      {/* Tür seçimi */}
      <Card className="mb-4">
        <h3 className="heading text-lg mb-3">1. Ne İçe Aktaracaksınız?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {IMPORT_TYPES.map(t => (
            <button key={t.id}
              onClick={() => { setEntity(t.id); reset(); }}
              data-testid={`import-type-${t.id}`}
              className={`text-left p-4 rounded-lg border-2 transition-colors ${entity === t.id ? "border-[#2D4A36] bg-[#E4EEE6]" : "border-[#E5E2DC] bg-white hover:border-[#B4C1B7]"}`}>
              <div className="font-semibold text-[#1A1D1A] mb-1">{t.label}</div>
              <div className="text-xs text-[#5e675e]">{t.desc}</div>
              <div className="text-[10px] text-[#8a938c] mt-2 italic">
                Sütun örneği: {t.sample}
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Dosya seçimi */}
      {!preview && !result && (
        <Card className="mb-4">
          <h3 className="heading text-lg mb-3">2. Excel Dosyasını Seç</h3>
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onFileChoose(f);
            }}
            data-testid="import-dropzone"
            className={`block cursor-pointer border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? "border-[#2D4A36] bg-[#E4EEE6]" : "border-[#E5E2DC] hover:border-[#B4C1B7] bg-[#FAF8F4]"}`}>
            <UploadSimple size={36} weight="duotone" className="mx-auto mb-2 text-[#5e675e]" />
            <div className="font-semibold text-[#1A1D1A] mb-1">
              Excel dosyasını buraya bırakın veya tıklayarak seçin
            </div>
            <div className="text-xs text-[#5e675e]">
              .xlsx formatı · Max 10 MB · İlk satır başlık olmalı
            </div>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => onFileChoose(e.target.files?.[0])}
            />
          </label>
        </Card>
      )}

      {/* Önizleme */}
      {preview && (
        <Card className="mb-4" testId="import-preview">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="heading text-lg">3. Önizleme — {preview.entity_label}</h3>
              <div className="text-sm text-[#5e675e]">
                {preview.total_rows.toLocaleString("tr-TR")} satır bulundu · {Object.keys(preview.mapping).length} alan otomatik eşlendi
              </div>
            </div>
            <button onClick={reset} className="text-sm text-[#8a938c] hover:text-[#1A1D1A] px-2" data-testid="import-reset">
              <XCircle size={16} className="inline mr-1" /> Vazgeç
            </button>
          </div>

          {preview.warnings?.length > 0 && (
            <div className="mb-3 p-3 rounded bg-[#fbeee9] border border-[#f2d4cb] text-sm text-[#8B3A29]" data-testid="import-warnings">
              ⚠️ {preview.warnings.join(" · ")}
            </div>
          )}

          {/* Sütun eşleşme */}
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#6b746c] mb-2">Otomatik Sütun Eşleşmesi</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(preview.mapping).map(([canon, idx]) => (
                <div key={canon} className="text-xs bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36] rounded px-2 py-1">
                  <span className="italic">{preview.headers[idx]}</span>
                  <ArrowRight size={10} className="inline mx-1" />
                  <b>{canon}</b>
                </div>
              ))}
            </div>
          </div>

          {/* Örnek satırlar */}
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#6b746c] mb-2">Örnek Kayıtlar (ilk 10)</div>
            <div className="overflow-x-auto border border-[#E5E2DC] rounded">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#FAF8F4] border-b border-[#EEECE5]">
                    {Object.keys(preview.mapping).map(k => (
                      <th key={k} className="p-2 text-left font-semibold">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((row, i) => (
                    <tr key={i} className={i % 2 ? "bg-[#FBFAF7]" : ""}>
                      {Object.keys(preview.mapping).map(k => (
                        <td key={k} className="p-2 border-t border-[#F5F2EC] tabnum">
                          {row[k] === null || row[k] === undefined ? <span className="text-[#c4c9c1]">—</span> : String(row[k])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-[#EEECE5]">
            <Button variant="secondary" onClick={reset}>İptal</Button>
            <Button onClick={apply} disabled={applying || preview.missing_required?.length > 0} data-testid="import-apply-btn">
              {applying ? "İçe aktarılıyor..." : `✓ ${preview.total_rows} kaydı içe aktar`}
            </Button>
          </div>
        </Card>
      )}

      {/* Sonuç */}
      {result && (
        <Card className="mb-4" testId="import-result">
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle size={28} weight="fill" className="text-[#417251] shrink-0 mt-0.5" />
            <div>
              <h3 className="heading text-lg">İçe Aktarım Tamamlandı</h3>
              <div className="text-sm text-[#5e675e]">{result.entity_label}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-[#E4EEE6] rounded">
              <div className="text-xs uppercase tracking-widest text-[#5e675e]">Eklendi</div>
              <div className="text-2xl font-bold text-[#2D4A36] tabnum" data-testid="import-imported-count">{result.imported}</div>
            </div>
            <div className="p-3 bg-[#FDF5E5] rounded">
              <div className="text-xs uppercase tracking-widest text-[#5e675e]">Atlandı</div>
              <div className="text-2xl font-bold text-[#8B6914] tabnum">{result.skipped}</div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="p-3 bg-[#fbeee9] border border-[#f2d4cb] rounded text-sm text-[#8B3A29] mb-3">
              <div className="font-semibold mb-1">⚠ Hatalar ({result.errors.length}):</div>
              <ul className="text-xs list-disc pl-4 space-y-0.5">
                {result.errors.map((e, i) => <li key={`${i}-${e}`}>{e}</li>)}
              </ul>
            </div>
          )}
          <Button variant="secondary" onClick={reset}>Yeni Bir Dosya Yükle</Button>
        </Card>
      )}

      {/* Format örneği */}
      <Card>
        <h3 className="heading text-lg mb-3">📋 {currentType?.label} — Sütun Formatı</h3>
        <div className="text-sm text-[#5e675e] mb-3">
          Excel dosyanızın <b>ilk satırı başlık olmalı</b>. Sistem şu başlıkları otomatik tanır (Türkçe veya İngilizce):
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {entity === "personnel" && [
            ["name (zorunlu)", "Ad, Ad Soyad, İsim, name, full name"],
            ["position", "Görev, Pozisyon, position, title"],
            ["phone", "Telefon, Cep, phone, mobile"],
            ["national_id", "TC, TC Kimlik, national id, tckn"],
            ["base_salary", "Maaş, Baz Maaş, salary"],
            ["insurance_amount", "Sigorta, sigorta tutarı, insurance"],
            ["hire_date", "İşe Başlama, hire date, start date"],
          ].map(([k, v]) => (
            <div key={k} className="border border-[#EEECE5] rounded p-2 bg-[#FAF8F4]">
              <b className="text-[#2D4A36]">{k}</b><br/>
              <span className="text-[#5e675e]">{v}</span>
            </div>
          ))}
          {entity === "milk" && [
            ["date (zorunlu)", "Tarih, Gün, date · Format: 2024-03-15 veya 15.03.2024"],
            ["liters (zorunlu)", "Litre, Miktar, L, liters"],
            ["unit_price", "Birim Fiyat, Fiyat, price"],
            ["milked_animal_count", "Hayvan Sayısı, Sağılan, animal count"],
          ].map(([k, v]) => (
            <div key={k} className="border border-[#EEECE5] rounded p-2 bg-[#FAF8F4]">
              <b className="text-[#2D4A36]">{k}</b><br/>
              <span className="text-[#5e675e]">{v}</span>
            </div>
          ))}
          {entity === "stock" && [
            ["name (zorunlu)", "İsim, Ad, Kalem, Ürün, name"],
            ["unit (zorunlu)", "Birim, unit (kg/ton/adet/balya)"],
            ["category", "Kategori, Grup, category (yem/kaba yem/malzeme...)"],
            ["quantity", "Miktar, Adet, quantity"],
            ["unit_price", "Birim Fiyat, Fiyat, price"],
            ["count_frequency", "Sayım Periyodu, Periyot (haftalık/aylık)"],
            ["supplier", "Tedarikçi, supplier"],
          ].map(([k, v]) => (
            <div key={k} className="border border-[#EEECE5] rounded p-2 bg-[#FAF8F4]">
              <b className="text-[#2D4A36]">{k}</b><br/>
              <span className="text-[#5e675e]">{v}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
